import { getCookie, setCookie } from '../utils/cookieUtils';
import { findDifferences } from '../utils/dataUtils';

/**
 * Менеджер таблицы, объединяющий фильтрацию, сортировку, пагинацию и редактирование.
 */
export class TableManager {
  constructor({ table, tableName, urlSave, contextmenu, rowsPerPage = 10, log = false }) {
    /** @type {HTMLTableElement} */
    this.log = log;
    this.table = table;
    this.tableName = tableName;
    this.urlSave = urlSave;
    this.contextmenu = contextmenu;
    this.rowsPerPage = rowsPerPage;

    this.table.tableManager = this;
    $(this.table).data('TableManager', this);
    this.fieldset = table.closest('fieldset');
    this.isTable = this.table.tagName === 'TABLE';

    /** Элементы управления */
    this.editButton = table.querySelector('.edit-table');
    this.saveButton = table.querySelector('.save-table');
    this.sortButton = table.querySelector('.sort-table');
    this.togglePaginationButton = table.querySelector('.toggle-pagination');
    this.tableSort = table.querySelectorAll('.table-sort th');
    this.interface = table.querySelector('.interface');
    this.tableCaptionContainer = table.closestInParent('.table-caption-container');

    /** Структура и данные таблицы */
    this.defaultInputTableData = getDataAll(this.table);
    this.onFieldsetUpdate = this.onFieldsetUpdate.bind(this);
    this.defaultTableData = this.getTableData();
    this.filteredData = [...this.defaultTableData]; // Копия для фильтрации и сортировки
    this.currentPage = 1;
    this.totalPages = 1;

    /** Элементы пагинации */
    this.paginationContainer = null;
    this.prevButton = null;
    this.nextButton = null;
    this.currentPageSpan = null;
    this.totalPagesSpan = null;
    this.filterStorageKey = `tableFilters_${this.table.id}`;
    this.sortStorageKey = `tableSort_${this.table.id}`;
    this.colWidthStorageKey = `tableColWidths_${this.table.id}`;

    /** Observer для отслеживания видимости */
    this.columnResizeObserver = null;
    this.columnResizeInitialized = false;

    /** Для отслеживания изменений */
    this.isUpdating = false;
    this.paginationEnabled = getCookie('paginationEnabled', false) * 1;

    if (this.log) console.log(`Initializing TableManager for table ID: ${this.table.id}`);

    this.init = this.init.bind(this);
    this.saveTable = this.saveTable.bind(this);
    this?.fieldset?.addEventListener('fieldsetLoaded', this.init);
    if (typeof this.fieldset?.updateData !== 'undefined') {
      this.init();
    }
    if (!this.isTable) {
      this.init();
    }

    this.cleanupInterval = setInterval(() => this.cleanupMemory(), 10000);
  }

  saveSortToStorage(columnIndex, direction) {
    localStorage.setItem(this.sortStorageKey, JSON.stringify({ columnIndex, direction }));
    if (this.log) console.log('Sort state saved to localStorage:', { columnIndex, direction });
  }

  loadSortFromStorage() {
    const sortStr = localStorage.getItem(this.sortStorageKey);
    if (!sortStr) return;
    try {
      const { columnIndex, direction } = JSON.parse(sortStr);
      if (typeof columnIndex === 'number' && (direction === 'asc' || direction === 'desc')) {
        const header = this.table.querySelectorAll('.sortBy')[columnIndex];
        if (header) {
          // Ставим предыдущее состояние чтобы sortBy переключил на нужное
          // null → asc, asc → desc
          header.setAttribute('data-sort-dir', direction === 'desc' ? 'asc' : '');
          if (direction === 'asc') header.removeAttribute('data-sort-dir');
          this.sortBy(columnIndex, header);
        }
      }
    } catch (e) {
      if (this.log) console.error('Failed to parse sort from localStorage', e);
    }
  }

  saveFiltersToStorage() {
    const filterInputs = this.table.querySelectorAll('.table-search input[type="text"], .table-search select');
    const filters = {};
    filterInputs.forEach(input => {
      const key = input.dataset.key;
      if (key !== undefined && key !== null) {
        filters[key] = input.value;
      }
    });
    localStorage.setItem(this.filterStorageKey, JSON.stringify(filters));
    if (this.log) console.log('Filters saved to localStorage:', filters);
  }

  loadFiltersFromStorage() {
    const filtersStr = localStorage.getItem(this.filterStorageKey);
    if (!filtersStr) return;
    try {
      const filters = JSON.parse(filtersStr);
      const filterInputs = this.table.querySelectorAll('.table-search input[type="text"], .table-search select');
      filterInputs.forEach(input => {
        const key = input.dataset.key;
        if (filters[key] !== undefined) {
          input.value = filters[key];
        }
      });
      if (this.log) console.log('Filters loaded from localStorage:', filters);
    } catch (e) {
      if (this.log) console.error('Failed to parse filters from localStorage', e);
    }
  }


  cleanupMemory() {
    if (!document.body.contains(this.table)) {
      if (this.log) console.log(`Table ${this.tableName} не найдена. Очищаем память.`);

      // Очищаем все ссылки на DOM-элементы
      this.table = null;
      this.fieldset = null;
      this.editButton = null;
      this.saveButton = null;
      this.sortButton = null;
      this.togglePaginationButton = null;
      this.paginationContainer = null;
      this.prevButton = null;
      this.nextButton = null;
      this.currentPageSpan = null;
      this.totalPagesSpan = null;

      // Очищаем обработчики ресайза колонок
      if (this._resizeMouseMove) document.removeEventListener('mousemove', this._resizeMouseMove);
      if (this._resizeMouseUp) document.removeEventListener('mouseup', this._resizeMouseUp);
      this._resizeMouseMove = null;
      this._resizeMouseUp = null;

      // Отключаем Intersection Observer для отслеживания видимости
      if (this.columnResizeObserver) {
        this.columnResizeObserver.disconnect();
        this.columnResizeObserver = null;
      }

      // Очищаем массивы данных
      this.defaultInputTableData = null;
      this.defaultTableData = null;
      this.filteredData = null;

      // Останавливаем таймер очистки
      clearInterval(this.cleanupInterval);

      if (this.log) console.log(`Память освобождена.`);
    }
  }

  onFieldsetUpdate() {
    const test = Object.assign(findDifferences(this.defaultInputTableData, this.getTableInputData(), 1));

    if (Object.keys(test).length) {
      this.saveButton.disabled = false;
      this.saveButton.classList.toggle('btn-success', true);
      this.saveButton.classList.toggle('btn-secondary', false);
    } else {
      this.saveButton.disabled = true;
      this.saveButton.classList.toggle('btn-success', false);
      this.saveButton.classList.toggle('btn-secondary', true);
    }
  }

  _setTableDefultData() {
    this.defaultInputTableData = getDataAll(this.table);
  }

  getTableInputData() {
    return getDataAll(this.table);
  }

  /**
   * Извлекает данные из таблицы и кэширует их в виде массива объектов.
   * Каждый объект содержит элемент строки и массив значений ячеек.
   */
  getTableData() {
    if (!this.isTable) return [];
    const tbody = this.table.querySelector('tbody');
    if (!tbody) {
      console.error('No tbody found in the table.');
      return [];
    }

    const rows = Array.from(tbody.rows);
    const data = rows.map((row) => {
      const cells = Array.from(row.cells).map((cell) => cell.innerText.trim());
      return { rowElement: row, cells };
    });

    if (this.log) console.log(`Extracted ${data.length} rows from the table.`);
    return data;
  }

  init() {

    // Сразу переносим панель управления в нужное место
    if (this.tableCaptionContainer && this.interface) {
      const parent = this.interface.parentNode;
      while (this.tableCaptionContainer.firstChild) {
        this.tableCaptionContainer.removeChild(this.tableCaptionContainer.firstChild);
      }
      this.tableCaptionContainer.appendChild(this.interface);
      this.interface.classList.remove('d-none');

      if (parent && parent.children.length === 0 && parent.textContent.trim() === '') {
        parent.style.display = 'none';
        if (parent.parentElement && parent.parentElement.tagName === 'CAPTION' && parent.parentElement.children.length === 1 && parent.parentElement.textContent.trim() === '') {
          parent.parentElement.style.display = 'none';
        }
      }
    }
    if (!this.isTable) return;
    this.fieldset.removeEventListener('fieldsetUpdated', this.init);

    // Получаем список всех колонок с sorttype = 4
    this.columnsWithSortType4 = [];
    this.tableSort.forEach((th, idx) => {
      if (th.dataset.sorttype == 4) {
        this.columnsWithSortType4.push(idx);
      }
    });

    this.setupEditAndSaveButtons();
    this.initFilterAndSort();
    this.initColumnResizeWithVisibility();
    this.setupPagination();
    this.renderTable();
    this.initContextMenu();
    this._setTableDefultData();

    this.fieldset.addEventListener('fieldsetUpdated', this.onFieldsetUpdate);

    this.loadFiltersFromStorage();
    this.handleSearch();
    this.loadSortFromStorage();
  }

  initContextMenu() {
    new ContextMenu({
      options: this.contextmenu,
      target: this.table.querySelector('tbody'),
      autoActive: true
    });
  }

  setupEditAndSaveButtons() {
    if (this.sortButton) {
      this.sortButton.addEventListener('click', () => this._startSort());
    }
    if (this.saveButton) {
      this.saveButton.disabled = true;
      if (this.log) console.log('Save button disabled initially.');
      this.saveButton.addEventListener('click', this.saveTable);
    }

    if (this.editButton) {
      this.editButton.addEventListener('click', () => this.enterEditMode());
      if (this.log) console.log('Edit button event listener added.');
    }

    if (this.togglePaginationButton) {
      this.togglePaginationButton.addEventListener('click', () => this.togglePagination());
      if (this.log) console.log('Toggle pagination button event listener added.');
    }
  }

  togglePagination() {
    this.paginationEnabled = !this.paginationEnabled;
    setCookie('paginationEnabled', this.paginationEnabled * 1);

    if (this.log) console.log(`Pagination enabled: ${this.paginationEnabled}`);
    if (this.paginationContainer) {
      this.paginationContainer.style.display = this.paginationEnabled ? '' : 'none';
    }
    if (this.paginationEnabled) {
      this.currentPage = 1;
      this.updatePagination();
    } else {
      this.renderTable();
    }
  }

  _startSort() {
    console.log('_startSort');
    const tbody = this.table.querySelector('tbody');

    // Инициализация dragDropInstance, если ещё не инициализирован
    if (typeof tbody.dragDropInstance === 'undefined') {
      new DragDropElements(tbody);
      tbody.dragDropInstance.onEndDrop = () => {
        const inputs = tbody.querySelectorAll('input[name^="tableData["][name$="[priority]"]');

        inputs.forEach((input, index) => {
          const newValue = index + 1;
          input.value = newValue;
          const td = input.closest('td');
          const span = td.querySelector('span');
          if (span) {
            span.textContent = newValue;
          }
        });
      };
    }

    // Переключение состояния
    const isActive = tbody.classList.toggle('drag-container');
    tbody.dragDropInstance.disable = !isActive;

    // Логирование данных при отключении
    if (!isActive) {
      this._saveTable();
      // console.log(getDataForm(this.table));
    }
  }

  enterEditMode() {
    if (this.log) console.log('Entering edit mode.');
    if (this.editButton) {
      this.editButton.remove();
    }
    this.table.classList.add('mode-edit');
    this.table.querySelectorAll('input, textarea, select').forEach((ele) => {
      ele.hidden = false;
      if (this.log) console.log(`Element made visible:`, ele);
    });
  }

  _saveTable() {
    if (this.validateForm()) {
      if (this.log) console.log('Form validation passed. Sending data to save.');
      const dataToSave = Object.assign(findDifferences(this.defaultInputTableData, this.getTableInputData(), 1), { tableName: this.tableName });
      new SendData({
        data: dataToSave,
        url: this.urlSave
      });
    } else {
      if (this.log) console.log('Form validation failed. Save aborted.');
    }
  }

  saveTable() {
    if (this.log) console.log('Attempting to save table.');
    if (!this.saveButton.disabled) {
      this._saveTable();
    } else {
      if (this.log) console.log('Save button is disabled. Save action aborted.');
    }
  }

  validateForm() {
    if (this.fieldset && typeof this.fieldset._validateForm === 'function') {
      return this.fieldset._validateForm(this.table);
    }
    return true; // Если нет валидатора, считаем, что всё в порядке
  }

  initFilterAndSort() {
    this.initCheckbox();
    this.initFilters();
    this.initSort();
  }

  initCheckbox() {
    const filterInputs = this.table.querySelectorAll('.table-search input[type="checkbox"]');
    filterInputs.forEach((input) => {
      input.addEventListener('change', this.handleCheckboxAll.bind(this));
    });

    const selectAll = this.table.querySelector('.select-all-checkbox');
    if (selectAll) {
      selectAll.addEventListener('change', (e) => {
        const status = e.target.checked;
        this.table.querySelectorAll('tbody input[name^="[checkbox_primary_key"]').forEach(cb => {
          cb.checked = status;
          cb.dispatchEvent(new Event('change', { bubbles: true }));
        });
        
        if (this.filteredData) {
          this.filteredData.forEach(row => {
            const cb = row.rowElement.querySelector('input[name^="[checkbox_primary_key"]');
            if (cb) {
              cb.checked = status;
              cb.dispatchEvent(new Event('change', { bubbles: true }));
            }
          });
        }
      });
    }

    if (this.log) console.log('Checkbox event listeners added.');
  }

  initFilters() {
    const filterInputs = this.table.querySelectorAll('.table-search input[type="text"], .table-search select');
    filterInputs.forEach((input) => {
      const eventType = input.tagName.toLowerCase() === 'select' ? 'change' : 'keyup';
      input.addEventListener(
        eventType,
        this.debounce(() => this.handleSearch(), 300)
      );
    });
    if (this.log) console.log('Filter event listeners added.');
  }

  initSort() {
    const sortableHeaders = this.table.querySelectorAll('.sortBy');
    sortableHeaders.forEach((header, idx) => {
      header.addEventListener('click', () => this.sortBy(idx, header));
    });
    if (this.log) console.log('Sort event listeners added.');
  }

  handleCheckboxAll(event) {
    const input = event.target;
    const key = input.dataset.key;
    this.filteredData.forEach((row) => {
      const cell = row.rowElement.querySelectorAll('td')[key];

      const checkbox = cell.querySelector('input[type="checkbox"]');
      if (!checkbox) return;
      checkbox.checked = input.checked;
    });
  }

  handleSearch() {
    if (this.log) console.log('Handling search/filter.');
    const filterInputs = this.table.querySelectorAll('.table-search input[type="text"], .table-search select');
    this.table.querySelectorAll('.table-search input[type="checkbox"]').forEach((checkbox) => (checkbox.checked = false));
    this.filteredData = this.defaultTableData.filter((rowData) => {
      return Array.from(filterInputs).every((input) => {
        const key = input.dataset.key;
        if (key === undefined || key === null) return true; // Если нет ключа, игнорируем фильтр

        const searchText = input.value.toLowerCase().trim();
        if (searchText === '') return true; // Если нет текста, игнорируем фильтр

        const cellTextRaw = rowData.cells[key].toLowerCase().replace(/\u200a/g, '');
        const typeSearch = this.tableSort[key].dataset.sorttype ? this.tableSort[key].dataset.sorttype : this.typeSearch(searchText);

        switch (typeSearch) {
          case '1': // Число
          case 1: // Число
            const cellTextNumeric = cellTextRaw.replace(/ /g, '').replace(/'/g, '').replace(/,/g, '.');
            return this.performNumericComparison(searchText, cellTextNumeric);
          case '2':
          case 2: // Дата
            const cellTextDate = cellTextRaw.replace(/ /g, '');
            return this.performDateComparison(searchText, cellTextDate);
          case 'split text':
            return searchText.split(' ').some((text) => cellTextRaw.includes(text));
          default: // Текст
            // Для текстового поиска оставляем запятые и пробелы как есть
            return cellTextRaw.includes(searchText);
        }
      });
    });
    
    if (this.log) console.log(`Filtered data count: ${this.filteredData.length}`);
    this.currentPage = 1; // Сброс на первую страницу после фильтрации
    this.updatePagination();
    this.saveFiltersToStorage();
  }

  typeSearch(searchText) {
    if (searchText.length === 0) return 0;

    const operator = searchText[0];
    const numericSearchPattern = parseFloat(searchText.substring(1).replace(/'/g, '').replace(/,/g, '.'));
    const datePattern = /^\d{2}\.\d{2}\.\d{4}(?:\s+\d{2}:\d{2}:\d{2})?$/;

    if ((operator === '>' || operator === '<') && datePattern.test(searchText.substring(1))) {
      return 2; // Дата
    } else if (!isNaN(numericSearchPattern) && isFinite(numericSearchPattern) && (operator === '>' || operator === '<')) {
      return 1; // Число
    }
    return 0; // Текст
  }

  performNumericComparison(searchText, rowText) {
    const operator = searchText[0];
    const searchNumber = parseFloat(searchText.substring(1).replace(/'/g, '').replace(/,/g, '.'));
    const rowNumber = parseFloat(rowText);
    if (operator === '>') {
      return rowNumber > searchNumber;
    } else if (operator === '<') {
      return rowNumber < searchNumber;
    } else if (operator === '=') {
      return rowNumber == searchNumber;
    } else {
      const a = parseFloat(searchText.replace(/'/g, '').replace(/,/g, '.'));
      const b = parseFloat(rowText);
      if (!isNaN(a) && !isNaN(b)) {
        return rowText.startsWith(searchText);
      } else {
        return false;
      }
    }
    // ГСС 2025-03-26
    // Идеально! Не трогать.
  }

  performDateComparison(searchText, rowText) {
    const operator = searchText[0];
    const searchDateText = searchText.substring(1).trim();

    const parseDateTime = (text) => {
      if (!text) return new Date(0);
      const [datePart, timePart] = text.split(' ');
      const [day, month, year] = (datePart || '').split('.').map((num) => parseInt(num, 10));
      let hours = 0, minutes = 0, seconds = 0;
      if (timePart) {
        const t = timePart.split(':').map((n) => parseInt(n, 10));
        hours = t[0] || 0; minutes = t[1] || 0; seconds = t[2] || 0;
      }
      return new Date(year || 0, (month || 1) - 1, day || 1, hours, minutes, seconds);
    };

    const searchDate = parseDateTime(searchDateText);
    const rowDate = parseDateTime(rowText);

    if (operator === '>') {
      return rowDate > searchDate;
    } else if (operator === '<') {
      return rowDate < searchDate;
    }
    return false;
  }

  resetSort() {
    this.table.querySelectorAll('.sortBy').forEach((th) => {
      th.removeAttribute('data-sort-dir');
      const icon = th.querySelector('i.fa');
      if (icon) icon.className = 'fa';
    });
    localStorage.removeItem(this.sortStorageKey);
    this.handleSearch();
  }

  sortBy(n, header) {
    const dataType = parseFloat(header.dataset.sorttype) || 0;
    if (this.log) console.log(`Sorting by column index: ${n}, dataType: ${dataType}`);

    // Сбрасываем состояния сортировки у других заголовков
    this.table.querySelectorAll('.sortBy').forEach((th) => {
      if (th !== header) {
        th.removeAttribute('data-sort-dir');
        const icon = th.querySelector('i.fa');
        if (icon) icon.className = 'fa';
      }
    });

    // Цикл: нет атрибута → asc → desc → сброс (удаляем атрибут)
    const current = header.getAttribute('data-sort-dir');
    let sortDirection;
    if (!current) {
      sortDirection = 'asc';
    } else if (current === 'asc') {
      sortDirection = 'desc';
    } else {
      // desc → сброс
      this.resetSort();
      return;
    }

    header.setAttribute('data-sort-dir', sortDirection);
    this.saveSortToStorage(n, sortDirection);

    // Обновляем иконку
    const icon = header.querySelector('i.fa');
    if (icon) {
      icon.className = 'fa';
      icon.classList.add(sortDirection === 'asc' ? 'fa-arrow-up' : 'fa-arrow-down');
    }

    // Сортируем
    this.filteredData.sort((a, b) => {
      const aValue = this.getCellValue(a, n, dataType);
      const bValue = this.getCellValue(b, n, dataType);

      if (dataType === 1 || dataType === 2) {
        return aValue - bValue;
      }
      return aValue.localeCompare(bValue);
    });

    if (sortDirection === 'desc') {
      this.filteredData.reverse();
    }

    if (this.log) console.log(`Table sorted by column ${n}, direction: ${sortDirection}`);
    this.currentPage = 1;
    this.updatePagination();
  }

  getCellValue(rowData, index, dataType) {
    const cellValue = rowData.cells[index].trim();
    switch (dataType) {
      case 1: // Число
        return parseFloat(cellValue.replace(/[^-\d.]/g, '')) || 0;
      case 2: // Дата (поддерживаем 'DD.MM.YYYY' и 'DD.MM.YYYY HH:MM:SS')
        const [datePart, timePart] = cellValue.split(' ');
        const dateParts = (datePart || '').split('.');
        if (dateParts.length === 3) {
          const day = parseInt(dateParts[0], 10);
          const month = parseInt(dateParts[1], 10) - 1;
          const year = parseInt(dateParts[2], 10);
          let hours = 0, minutes = 0, seconds = 0;
          if (timePart) {
            const t = timePart.split(':').map((n) => parseInt(n, 10));
            hours = t[0] || 0; minutes = t[1] || 0; seconds = t[2] || 0;
          }
          return new Date(year, month, day, hours, minutes, seconds);
        }
        return new Date(0);
      default: // Строка
        return cellValue.toLowerCase();
    }
  }

  setupPagination() {
    if (this.log) console.log('Setting up pagination.');

    // Создаем контейнер для пагинации
    this.paginationContainer = document.createElement('div');
    this.paginationContainer.classList.add('pagination-container');
    if (this.log) console.log('Pagination container created.');

    // Создаем кнопки и индикаторы
    this.prevButton = document.createElement('div');
    this.prevButton.innerHTML = '<i class="fa fa-arrow-left"></i>'; // Используйте нужную иконку
    this.prevButton.classList.add('pagination-btn');
    this.prevButton.addEventListener('click', () => this.goToPreviousPage());

    // Создание кнопки "Вперед" с иконкой
    this.nextButton = document.createElement('div');
    this.nextButton.innerHTML = '<i class="fa fa-arrow-right"></i>'; // Используйте нужную иконку
    this.nextButton.classList.add('pagination-btn');
    this.nextButton.addEventListener('click', () => this.goToNextPage());

    this.currentPageSpan = document.createElement('span');
    this.currentPageSpan.textContent = '1';
    if (this.log) console.log('Current page span created.');

    this.totalPagesSpan = document.createElement('span');
    this.totalPagesSpan.textContent = '1';
    if (this.log) console.log('Total pages span created.');

    // Добавляем элементы в контейнер
    this.paginationContainer.appendChild(this.prevButton);
    this.paginationContainer.appendChild(this.currentPageSpan);
    this.paginationContainer.appendChild(document.createTextNode(' / '));
    this.paginationContainer.appendChild(this.totalPagesSpan);
    this.paginationContainer.appendChild(this.nextButton);
    if (this.log) console.log('Pagination buttons and spans appended to container.');

    // Вставляем контейнер после таблицы
    this.table.parentNode.insertBefore(this.paginationContainer, this.table.nextSibling);
    if (this.log) console.log('Pagination container inserted into DOM.');

    // Обновляем состояние кнопок
    this.updatePagination();
  }

  updatePagination() {
    if (!this.paginationEnabled) {
      this.paginationContainer.style.display = 'none';
      this.renderTable();
      return;
    }
    if (this.isUpdating) return;
    this.isUpdating = true;

    if (this.log) console.log('Updating pagination.');
    this.totalPages = Math.max(1, Math.ceil(this.filteredData.length / this.rowsPerPage));
    this.totalPagesSpan.textContent = this.totalPages;
    if (this.log) console.log(`Total pages calculated: ${this.totalPages}`);

    // Скрываем контейнер пагинации, если все строки помещаются на одной странице
    if (this.paginationContainer) {
      this.paginationContainer.style.display = this.totalPages === 1 ? 'none' : '';
    }

    if (this.currentPage > this.totalPages) {
      if (this.log) console.log(`Current page (${this.currentPage}) exceeds total pages (${this.totalPages}). Adjusting to page 1.`);
      this.currentPage = 1;
    }
    this.currentPageSpan.textContent = this.currentPage;
    if (this.log) console.log(`Current page set to: ${this.currentPage}`);

    this.updatePaginationButtons();
    this.renderTable();

    this.isUpdating = false;
  }

  updatePaginationButtons() {
    if (this.log) console.log('Updating pagination buttons state.');
    this.prevButton.disabled = this.currentPage === 1;
    this.nextButton.disabled = this.currentPage === this.totalPages;
    if (this.log) console.log(`Previous button disabled: ${this.prevButton.disabled}`);
    if (this.log) console.log(`Next button disabled: ${this.nextButton.disabled}`);
  }

  goToPreviousPage() {
    if (this.currentPage > 1) {
      this.currentPage--;
      if (this.log) console.log(`Navigated to previous page: ${this.currentPage}`);
      this.updatePagination();
    } else {
      if (this.log) console.log('Previous page action ignored: already on the first page.');
    }
  }

  goToNextPage() {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      if (this.log) console.log(`Navigated to next page: ${this.currentPage}`);
      this.updatePagination();
    } else {
      if (this.log) console.log('Next page action ignored: already on the last page.');
    }
  }

  renderTable() {
    if (this.togglePaginationButton) {
      this.togglePaginationButton.classList.toggle('btn-success', !!this.paginationEnabled);
      this.togglePaginationButton.classList.toggle('btn-warning', !this.paginationEnabled);
    }
    if (this.log) console.log('Rendering table.');
    const tbody = this.table.querySelector('tbody');
    if (!tbody) {
      console.error('No tbody found in the table.');
      return;
    }
    tbody.innerHTML = '';
    let rowsToRender;
    if (this.paginationEnabled) {
      const start = (this.currentPage - 1) * this.rowsPerPage;
      const end = start + this.rowsPerPage;
      rowsToRender = this.filteredData.slice(start, end);
    } else {
      rowsToRender = this.filteredData;
    }
    const fragment = document.createDocumentFragment();
    rowsToRender.forEach((rowData, idx) => {
      // Добавляем нумерацию в нужные столбцы (this.columnsWithSortType4)
      this.columnsWithSortType4.forEach((colIdx) => {
        const cell = rowData.rowElement.cells[colIdx];
        if (cell) {
          // Индекс относительно текущей страницы
          const rowNumber = this.paginationEnabled
            ? (this.currentPage - 1) * this.rowsPerPage + idx + 1
            : idx + 1;
          cell.textContent = rowNumber;
        }
      });
      fragment.appendChild(rowData.rowElement);
    });
    tbody.appendChild(fragment);
    if (this.log) {
      if (this.paginationEnabled) {
        console.log(`Rendered ${rowsToRender.length} rows on page ${this.currentPage}.`);
      } else {
        console.log(`Rendered all ${rowsToRender.length} rows (pagination disabled).`);
      }
    }
  }

  // ═══════════════════════════════════════════════════════════════
  // Изменение ширины колонок мышкой (drag на правой границе заголовка)
  // ═══════════════════════════════════════════════════════════════

  /**
   * Инициализирует Intersection Observer для отслеживания видимости таблицы.
   * Когда таблица становится видимой, определяет реальные размеры колонок.
   * Инициализация происходит ровно один раз.
   */
  initColumnResizeWithVisibility() {
    // Подвешиваем Intersection Observer для отслеживания видимости
    this.columnResizeObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !this.columnResizeInitialized && this.table) {
          const tableWidth = this.table.getBoundingClientRect().width;
          if (tableWidth > 0) {
            this.setupColumnResizeHandlers();
            this.columnResizeInitialized = true;
            if (this.log) console.log('Column resize initialized via Intersection Observer.');
          }
        }
      });
    }, {
      threshold: 0.01,
      root: null,
      rootMargin: '0px'
    });

    this.columnResizeObserver.observe(this.table);
    if (this.log) console.log('Intersection Observer attached to table element.');
  }

  /**
   * Инициализирует возможность изменения ширины колонок перетаскиванием.
   * Добавляет resize-ручки на правую границу каждого заголовка таблицы.
   * Сохраняет ширины в localStorage.
   */
  setupColumnResizeHandlers() {
    const headerRow = this.table.querySelector('.table-sort, thead tr');
    if (!headerRow) return;

    const headers = headerRow.querySelectorAll('th');
    if (headers.length < 2) return;

    const tableWidth = this.table.getBoundingClientRect().width;

    // Захватываем текущие вычисленные ширины как проценты от таблицы
    const computedPcts = Array.from(headers).map(th =>
      (th.getBoundingClientRect().width / tableWidth * 100)
    );

    // Применяем базовые ширины в процентах
    headers.forEach((th, i) => {
      th.style.width = computedPcts[i] + '%';
    });

    // Фиксируем layout — ширины уже заданы
    this.table.style.tableLayout = 'fixed';

    // Поверх базовых загружаем сохранённые (если есть)
    this.loadColumnWidths(headers);

    // Ресайзеры на все колонки КРОМЕ последней
    headers.forEach((th, index) => {
      if (index === headers.length - 1) return;

      // Не добавляем ресайзер, если он уже существует
      if (th.querySelector('.col-resizer')) return;

      const resizer = document.createElement('div');
      resizer.className = 'col-resizer';
      th.style.position = 'relative';
      th.appendChild(resizer);

      const onMouseDown = (e) => {
        e.preventDefault();
        e.stopPropagation();

        const startX = e.pageX;
        const currentTableWidth = this.table.getBoundingClientRect().width;
        const startPct = parseFloat(th.style.width);
        const nextTh = headers[index + 1];
        const nextStartPct = parseFloat(nextTh.style.width);

        resizer.classList.add('col-resizer--active');
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';

        const onMouseMove = (e) => {
          const diffPx = e.pageX - startX;
          const diffPct = (diffPx / currentTableWidth) * 100;

          const newPct = Math.max(2, startPct + diffPct);
          const newNextPct = Math.max(2, nextStartPct - diffPct);

          // Не даём сумме двух колонок выйти за исходную
          if (newPct + newNextPct > startPct + nextStartPct + 0.01) return;

          th.style.width = newPct + '%';
          nextTh.style.width = newNextPct + '%';
        };

        const onMouseUp = () => {
          resizer.classList.remove('col-resizer--active');
          document.body.style.cursor = '';
          document.body.style.userSelect = '';

          document.removeEventListener('mousemove', onMouseMove);
          document.removeEventListener('mouseup', onMouseUp);

          // Подавляем click, который всплывёт на th после mouseup (иначе сработает сортировка)
          th.addEventListener('click', (e) => {
            e.stopPropagation();
            e.preventDefault();
          }, { once: true, capture: true });

          this.saveColumnWidths(headers);
          if (this.log) console.log(`Column ${index} resized to ${th.style.width}`);
        };

        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
      };

      resizer.addEventListener('mousedown', onMouseDown);
    });

    // Инъекция стилей (один раз на документ)
    if (!document.getElementById('col-resizer-styles')) {
      const style = document.createElement('style');
      style.id = 'col-resizer-styles';
      style.textContent = `
        .col-resizer {
          position: absolute;
          right: -3px;
          top: 0;
          bottom: 0;
          width: 6px;
          cursor: col-resize;
          z-index: 10;
          transition: background-color 0.15s;
        }
        .col-resizer:hover,
        .col-resizer--active {
          background-color: rgba(13, 110, 253, 0.4);
        }
      `;
      document.head.appendChild(style);
    }

    if (this.log) console.log('Column resize handlers initialized.');
  }

  /**
   * Сохраняет текущие ширины колонок в localStorage.
   * @param {NodeListOf<HTMLTableCellElement>} headers
   */
  saveColumnWidths(headers) {
    const widths = Array.from(headers).map(th => th.style.width || '');
    localStorage.setItem(this.colWidthStorageKey, JSON.stringify(widths));
    if (this.log) console.log('Column widths saved:', widths);
  }

  /**
   * Загружает ширины колонок из localStorage и применяет к заголовкам.
   * @param {NodeListOf<HTMLTableCellElement>} headers
   */
  loadColumnWidths(headers) {
    const stored = localStorage.getItem(this.colWidthStorageKey);
    if (!stored) return;
    try {
      const widths = JSON.parse(stored);
      headers.forEach((th, i) => {
        if (widths[i]) th.style.width = widths[i];
      });
      if (this.log) console.log('Column widths loaded:', widths);
    } catch (e) {
      if (this.log) console.error('Failed to load column widths:', e);
    }
  }

  /**
   * Функция дебаунса для предотвращения избыточных вызовов.
   * @param {Function} func Функция, которую нужно дебаунсить.
   * @param {number} wait Время ожидания в миллисекундах.
   * @returns {Function} Дебаунс-функция.
   */
  debounce(func, wait) {
    let timeout;
    return function (...args) {
      const later = () => {
        clearTimeout(timeout);
        func.apply(this, args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
}
